import { AfterViewInit, Component, ElementRef, OnDestroy, computed, inject, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { homeRouteForOfficer } from '../../core/routing/portal-home';

type Lang = 'en' | 'hi';
type FontScale = 'sm' | 'md' | 'lg';

interface LandingCopy {
  skip: string;
  screenReader: string;
  highContrast: string;
  gov: string;
  org: string;
  login: string;
  dashboard: string;
  citizen: string;
  employee: string;
  navHow: string;
  navServices: string;
  navFaq: string;
  brandMark: string;
  brandTitle: string;
  support: string;
  citizenLogin: string;
  employeeLogin: string;
  scrollHint: string;
  demoTitle: string;
  demoPlay: string;
  demoKicker: string;
  demoPoint: string;
  howEyebrow: string;
  howTitle: string;
  howLead: string;
  step1Title: string;
  step1Body: string;
  step2Title: string;
  step2Body: string;
  step3Title: string;
  step3Body: string;
  step4Title: string;
  step4Body: string;
  servicesEyebrow: string;
  servicesTitle: string;
  servicesLead: string;
  tabCitizen: string;
  tabEmployee: string;
  citizenPoints: string[];
  employeePoints: string[];
  statusEyebrow: string;
  statusTitle: string;
  statusLead: string;
  statuses: Array<{ code: string; label: string; hint: string }>;
  needEyebrow: string;
  needTitle: string;
  needLead: string;
  needItems: string[];
  faqEyebrow: string;
  faqTitle: string;
  faqs: Array<{ q: string; a: string }>;
  helpTitle: string;
  helpBody: string;
  helpDesk: string;
  helpHours: string;
  helpMail: string;
  footMain: string;
  footAuth: string;
  footLinks: string;
}

const COPY: Record<Lang, LandingCopy> = {
  en: {
    skip: 'Skip to main content',
    screenReader: 'Screen reader access',
    highContrast: 'High contrast',
    gov: 'Government of Uttar Pradesh',
    org: 'Deoria Administration',
    login: 'Login',
    dashboard: 'Go to dashboard',
    citizen: 'Citizen',
    employee: 'Employee',
    navHow: 'How it works',
    navServices: 'Services',
    navFaq: 'FAQ',
    brandMark: 'District Grievance Redressal',
    brandTitle: 'Jansunwai',
    support:
      'Transparent grievance redressal for citizens and district officers — file, hear, and resolve.',
    citizenLogin: 'Citizen login',
    employeeLogin: 'Login',
    scrollHint: 'Scroll to explore',
    demoTitle: 'See Jansunwai in action',
    demoPlay: 'Play portal walkthrough',
    demoKicker: 'Watch this',
    demoPoint: 'A short walkthrough of how citizens and officers use the portal.',
    howEyebrow: 'How Jansunwai works',
    howTitle: 'From intake to resolution under one district process.',
    howLead: 'Every step leaves a clear trail — so citizens and officers share the same picture of progress.',
    step1Title: 'Intake',
    step1Body: 'Citizens file online or at the desk; officers register walk-in complaints with supporting papers.',
    step2Title: 'Review & route',
    step2Body: 'DM verifies details, assigns the right desk role, and escalates when time or gravity demands it.',
    step3Title: 'Investigate',
    step3Body: 'Assigned officers collect facts, upload notes, and submit an investigation report for review.',
    step4Title: 'Hear & resolve',
    step4Body: 'Peshkar schedules hearings; DM records outcomes and closes the case with a reasoned order.',
    servicesEyebrow: 'Who uses Jansunwai',
    servicesTitle: 'One portal. Two desks. Shared accountability.',
    servicesLead: 'Choose your path — the same case trail stays visible to both sides.',
    tabCitizen: 'For citizens',
    tabEmployee: 'For officers',
    citizenPoints: [
      'File a grievance with documents and location details',
      'Sign in to follow updates on your filed cases',
      'Receive updates when hearings are scheduled',
      'See the final order when the case is closed',
    ],
    employeePoints: [
      'DM assigns work by desk role across departments',
      'Officers investigate and submit reports online',
      'Peshkar manages roster and hearing slots',
      'Resolution dashboard keeps outcomes on record',
    ],
    statusEyebrow: 'Status guide',
    statusTitle: 'What each stage means',
    statusLead: 'Tap a stage to see what happens next in the district workflow.',
    statuses: [
      { code: 'Submitted', label: 'Submitted', hint: 'Your grievance is registered and awaiting first review.' },
      { code: 'Under review', label: 'Under review', hint: 'District staff are verifying details and deciding the route.' },
      { code: 'Routed', label: 'Routed', hint: 'Assigned to a desk officer for field investigation.' },
      { code: 'Hearing', label: 'Hearing', hint: 'A hearing slot is fixed; attend with papers if called.' },
      { code: 'Resolved', label: 'Resolved', hint: 'A reasoned order is on record; the case may be closed.' },
    ],
    needEyebrow: 'Before you file',
    needTitle: 'Keep these ready',
    needLead: 'Complete filings move faster through verification and assignment.',
    needItems: [
      'Full name, mobile number, and address of the applicant',
      'Clear subject and a short description of the grievance',
      'Department or locality concerned (village / ward / tehsil)',
      'Supporting papers or photos, if available (PDF / JPG)',
      'Any prior reference number from an earlier complaint',
    ],
    faqEyebrow: 'Help',
    faqTitle: 'Frequently asked questions',
    faqs: [
      {
        q: 'How do I check the status of my grievance?',
        a: 'Sign in to your citizen account to view case status and updates, or visit the District Grievance Desk with your application number.',
      },
      {
        q: 'Who hears my case?',
        a: 'Investigation is done by the assigned desk. Hearings are scheduled by Peshkar and conducted under the District Magistrate’s process.',
      },
      {
        q: 'Can I update documents after filing?',
        a: 'Yes — contact the grievance desk or the assigned officer to submit additional papers when requested or before hearing.',
      },
      {
        q: 'What if my case is urgent?',
        a: 'Mark urgency while filing and state the reason. The DM desk may escalate priority during review and routing.',
      },
    ],
    helpTitle: 'Need help at the counter?',
    helpBody: 'Visit the Jansunwai / Public Grievance desk at the District Collectorate during working hours.',
    helpDesk: 'District Grievance Desk',
    helpHours: 'Mon–Sat · 10:00 – 17:00',
    helpMail: 'support@jansunwai.gov.in',
    footMain: '© Deoria Administration · Jansunwai Grievance Redressal',
    footAuth: 'Authorised use only',
    footLinks: 'Accessibility · Privacy · Terms of use',
  },
  hi: {
    skip: 'मुख्य सामग्री पर जाएँ',
    screenReader: 'स्क्रीन रीडर पहुँच',
    highContrast: 'उच्च कंट्रास्ट',
    gov: 'उत्तर प्रदेश सरकार',
    org: 'देवरिया प्रशासन',
    login: 'लॉगिन',
    dashboard: 'डैशबोर्ड पर जाएँ',
    citizen: 'नागरिक',
    employee: 'कर्मचारी',
    navHow: 'प्रक्रिया',
    navServices: 'सेवाएँ',
    navFaq: 'प्रश्न',
    brandMark: 'जनसुनवाई',
    brandTitle: 'जिला शिकायत निवारण',
    support:
      'नागरिकों और जिला अधिकारियों के लिए पारदर्शी शिकायत निवारण — दर्ज करें, सुनवाई करें और निपटान करें।',
    citizenLogin: 'नागरिक लॉगिन',
    employeeLogin: 'लॉगिन',
    scrollHint: 'और जानने के लिए स्क्रॉल करें',
    demoTitle: 'जनसुनवाई कैसे काम करती है',
    demoPlay: 'पोर्टल वॉकथ्रू चलाएँ',
    demoKicker: 'यह देखें',
    demoPoint: 'नागरिक और अधिकारी पोर्टल का उपयोग कैसे करते हैं — संक्षिप्त वॉकथ्रू।',
    howEyebrow: 'जनसुनवाई कैसे काम करती है',
    howTitle: 'एक जिला प्रक्रिया में दर्ज करने से निपटान तक।',
    howLead: 'हर चरण का स्पष्ट रिकॉर्ड रहता है — नागरिक और अधिकारी एक ही प्रगति देखें।',
    step1Title: 'प्राप्ति',
    step1Body: 'नागरिक ऑनलाइन या काउंटर पर शिकायत दर्ज करें; अधिकारी वॉक-इन शिकायतें दस्तावेज़ों सहित पंजीकृत करें।',
    step2Title: 'समीक्षा व मार्ग',
    step2Body: 'जिलाधिकारी विवरण सत्यापित करें, सही डेस्क भूमिका सौंपें, और आवश्यकता पर आगे बढ़ाएँ।',
    step3Title: 'जाँच',
    step3Body: 'नियुक्त अधिकारी तथ्य एकत्र करें, नोट अपलोड करें, और समीक्षा हेतु जाँच रिपोर्ट जमा करें।',
    step4Title: 'सुनवाई व निपटान',
    step4Body: 'पेशकार सुनवाई निर्धारित करें; जिलाधिकारी परिणाम दर्ज कर आदेश के साथ मामला बंद करें।',
    servicesEyebrow: 'कौन उपयोग करता है',
    servicesTitle: 'एक पोर्टल। दो डेस्क। साझा जवाबदेही।',
    servicesLead: 'अपना मार्ग चुनें — दोनों पक्षों को एक ही केस ट्रेल दिखती है।',
    tabCitizen: 'नागरिकों के लिए',
    tabEmployee: 'अधिकारियों के लिए',
    citizenPoints: [
      'दस्तावेज़ और स्थान विवरण के साथ शिकायत दर्ज करें',
      'दर्ज मामलों के अपडेट देखने के लिए साइन इन करें',
      'सुनवाई निर्धारित होने पर अपडेट पाएँ',
      'मामला बंद होने पर अंतिम आदेश देखें',
    ],
    employeePoints: [
      'जिलाधिकारी विभागों में डेस्क भूमिका से कार्य सौंपें',
      'अधिकारी जाँच करें और ऑनलाइन रिपोर्ट जमा करें',
      'पेशकार रोस्टर और सुनवाई स्लॉट प्रबंधित करें',
      'निपटान डैशबोर्ड परिणामों का रिकॉर्ड रखे',
    ],
    statusEyebrow: 'स्थिति मार्गदर्शिका',
    statusTitle: 'प्रत्येक चरण का अर्थ',
    statusLead: 'अगला क्या होता है देखने के लिए चरण चुनें।',
    statuses: [
      { code: 'Submitted', label: 'दर्ज', hint: 'आपकी शिकायत पंजीकृत है और पहली समीक्षा की प्रतीक्षा में है।' },
      { code: 'Under review', label: 'समीक्षाधीन', hint: 'जिला कर्मचारी विवरण सत्यापित कर मार्ग तय कर रहे हैं।' },
      { code: 'Routed', label: 'सौंपा गया', hint: 'क्षेत्रीय जाँच हेतु डेस्क अधिकारी को सौंपा गया।' },
      { code: 'Hearing', label: 'सुनवाई', hint: 'सुनवाई स्लॉट तय; बुलाए जाने पर दस्तावेज़ लेकर आएँ।' },
      { code: 'Resolved', label: 'निपटान', hint: 'तर्कसंगत आदेश दर्ज; मामला बंद हो सकता है।' },
    ],
    needEyebrow: 'दर्ज करने से पहले',
    needTitle: 'ये तैयार रखें',
    needLead: 'पूर्ण आवेदन सत्यापन और आवंटन में तेज़ी लाते हैं।',
    needItems: [
      'आवेदक का पूरा नाम, मोबाइल और पता',
      'स्पष्ट विषय और शिकायत का संक्षिप्त विवरण',
      'संबंधित विभाग या क्षेत्र (गाँव / वार्ड / तहसील)',
      'सहायक दस्तावेज़ या फोटो, यदि हों (PDF / JPG)',
      'पिछली शिकायत का कोई संदर्भ संख्या',
    ],
    faqEyebrow: 'सहायता',
    faqTitle: 'अक्सर पूछे जाने वाले प्रश्न',
    faqs: [
      {
        q: 'शिकायत की स्थिति कैसे देखें?',
        a: 'स्थिति और अपडेट देखने के लिए अपने नागरिक खाते में साइन इन करें, या आवेदन संख्या लेकर जिला शिकायत डेस्क पर जाएँ।',
      },
      {
        q: 'मेरे मामले की सुनवाई कौन करता है?',
        a: 'जाँच नियुक्त डेस्क करती है। सुनवाई पेशकार निर्धारित करते हैं और जिलाधिकारी प्रक्रिया के अंतर्गत होती है।',
      },
      {
        q: 'दर्ज करने के बाद दस्तावेज़ अपडेट कर सकते हैं?',
        a: 'हाँ — अतिरिक्त दस्तावेज़ जमा करने के लिए शिकायत डेस्क या नियुक्त अधिकारी से संपर्क करें।',
      },
      {
        q: 'मामला अत्यावश्यक हो तो?',
        a: 'दर्ज करते समय अत्यावश्यकता चिह्नित करें और कारण लिखें। समीक्षा में जिलाधिकारी डेस्क प्राथमिकता बढ़ा सकते हैं।',
      },
    ],
    helpTitle: 'काउंटर पर सहायता चाहिए?',
    helpBody: 'कार्य दिवसों में जिला कलेक्टरेट के जनसुनवाई / लोक शिकायत डेस्क पर आएँ।',
    helpDesk: 'जिला शिकायत डेस्क',
    helpHours: 'सोम–शनि · 10:00 – 17:00',
    helpMail: 'support@jansunwai.gov.in',
    footMain: '© देवरिया प्रशासन · जनसुनवाई शिकायत निवारण',
    footAuth: 'केवल अधिकृत उपयोग',
    footLinks: 'सुलभता · गोपनीयता · उपयोग की शर्तें',
  },
};

const LANG_KEY = 'jansunwai.landing.lang';
const FONT_KEY = 'jansunwai.landing.font';
const CONTRAST_KEY = 'jansunwai.landing.contrast';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss',
})
export class LandingComponent implements AfterViewInit, OnDestroy {
  private readonly auth = inject(AuthService);
  private revealObserver: IntersectionObserver | null = null;

  readonly lang = signal<Lang>(this.readLang());
  readonly t = computed(() => COPY[this.lang()]);
  /**
   * Public CTA follows a *confirmed* session only. Leftover localStorage is
   * ignored until AuthService finishes GET /auth/session, so first open shows
   * Login instead of a false "Go to dashboard".
   */
  readonly signedIn = this.auth.isAuthenticated;
  /** Desk home (administration, dashboard, …) — not always `/dashboard`. */
  readonly entryLink = computed(() => {
    if (!this.signedIn()) return '/login';
    if (this.auth.needsRoleChoice()) return '/login';
    return homeRouteForOfficer(this.auth.officer());
  });
  readonly entryLabel = computed(() => (this.signedIn() ? this.t().dashboard : this.t().login));
  readonly fontScale = signal<FontScale>(this.readFont());
  readonly highContrast = signal(localStorage.getItem(CONTRAST_KEY) === '1');
  readonly serviceTab = signal<'citizen' | 'employee'>('citizen');
  /** Collapsed page nav on small screens. */
  readonly menuOpen = signal(false);
  readonly activeStatus = signal(0);
  readonly openFaq = signal<number | null>(0);
  readonly activeStep = signal(0);
  readonly demoPlaying = signal(false);
  private readonly demoVideo = viewChild<ElementRef<HTMLVideoElement>>('demoVideo');

  constructor() {
    document.documentElement.lang = this.lang() === 'hi' ? 'hi' : 'en';
  }

  ngAfterViewInit(): void {
    this.applyA11y();
    this.bindReveal();
  }

  ngOnDestroy(): void {
    this.revealObserver?.disconnect();
    this.demoVideo()?.nativeElement.pause();
  }

  setLang(next: Lang, event?: Event): void {
    event?.stopPropagation();
    this.lang.set(next);
    localStorage.setItem(LANG_KEY, next);
    document.documentElement.lang = next === 'hi' ? 'hi' : 'en';
    this.openFaq.set(0);
    this.activeStatus.set(0);
  }

  setFont(scale: FontScale, event?: Event): void {
    event?.stopPropagation();
    this.fontScale.set(scale);
    localStorage.setItem(FONT_KEY, scale);
    this.applyA11y();
  }

  toggleContrast(event?: Event): void {
    event?.stopPropagation();
    this.highContrast.update((v) => !v);
    localStorage.setItem(CONTRAST_KEY, this.highContrast() ? '1' : '0');
    this.applyA11y();
  }

  setServiceTab(tab: 'citizen' | 'employee'): void {
    this.serviceTab.set(tab);
  }

  selectStatus(index: number): void {
    this.activeStatus.set(index);
  }

  selectStep(index: number): void {
    this.activeStep.set(index);
  }

  toggleFaq(index: number): void {
    this.openFaq.update((cur) => (cur === index ? null : index));
  }

  toggleMenu(): void {
    this.menuOpen.update((v) => !v);
  }

  scrollTo(id: string, event?: Event): void {
    event?.preventDefault();
    this.menuOpen.set(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  playDemo(): void {
    void this.demoVideo()?.nativeElement.play();
  }

  onDemoPlay(): void {
    this.demoPlaying.set(true);
  }

  onDemoPause(): void {
    const el = this.demoVideo()?.nativeElement;
    this.demoPlaying.set(!!el && !el.paused && !el.ended);
  }

  onDemoEnded(): void {
    this.demoPlaying.set(false);
    const el = this.demoVideo()?.nativeElement;
    if (el) el.currentTime = 0;
  }

  private applyA11y(): void {
    const root = document.querySelector('.landing') as HTMLElement | null;
    if (!root) return;
    root.dataset['font'] = this.fontScale();
    root.dataset['contrast'] = this.highContrast() ? 'on' : 'off';
  }

  private bindReveal(): void {
    const nodes = document.querySelectorAll('.landing .reveal');
    if (!nodes.length || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => n.classList.add('in'));
      return;
    }
    this.revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            this.revealObserver?.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.14, rootMargin: '0px 0px -8% 0px' },
    );
    nodes.forEach((n) => this.revealObserver?.observe(n));
  }

  private readLang(): Lang {
    const saved = localStorage.getItem(LANG_KEY);
    return saved === 'hi' ? 'hi' : 'en';
  }

  private readFont(): FontScale {
    const saved = localStorage.getItem(FONT_KEY);
    return saved === 'sm' || saved === 'lg' ? saved : 'md';
  }
}
